import { FC, useState } from 'react';

import './PendingSync.css';
import { syncer } from '../../offline';
import { readQueue } from '../../offline/queue';
import type { SyncStatus } from '../../offline/syncer';
import { formatMoney } from '../../shared/functions/formatter';
import { Button, Dialog, EmptyState, Notice } from '../../ui';

type PendingSyncProps = {
  status: SyncStatus;
  onClose: () => void;
};

function when(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * What is still sitting on this device, and whether it made it to the server.
 *
 * Quarantined entries are shown rather than hidden: the customer paid for them,
 * so an operator has to be able to see that one did not reconcile and why.
 * The dialog's own backdrop, focus, Escape and scroll lock are the shared
 * Dialog's; this file owns the queue list and the sync control.
 */
const PendingSync: FC<PendingSyncProps> = ({ status, onClose }) => {
  const [syncing, setSyncing] = useState(false);
  const queue = readQueue();
  const pending = queue.filter((sale) => !sale.quarantined);
  const stuck = queue.filter((sale) => sale.quarantined);

  const syncNow = () => {
    setSyncing(true);
    syncer
      .flush()
      .catch(() => undefined)
      .finally(() => setSyncing(false));
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Saved on this device"
      description={
        <>
          {status.online ? 'Connected.' : 'No connection to the server.'}{' '}
          {pending.length === 0 ? 'Nothing waiting to sync.' : `${pending.length} sale${pending.length === 1 ? '' : 's'} waiting to sync.`}
        </>
      }
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            close
          </Button>
          <Button
            variant="primary"
            onClick={syncNow}
            disabled={syncing || pending.length === 0}
            title={status.online ? undefined : 'The server is unreachable right now.'}
          >
            {syncing ? 'syncing…' : 'sync now'}
          </Button>
        </>
      }
    >
      {status.lastError && <Notice variant="error">{status.lastError}</Notice>}

      {status.conflicts > 0 && (
        <Notice variant="warning">
          {status.conflicts} synced sale{status.conflicts === 1 ? '' : 's'} were priced differently from the current catalogue. They were
          recorded at the price the customer paid.
        </Notice>
      )}

      {pending.length === 0 && stuck.length === 0 ? (
        <EmptyState title="All sales have reached the server." />
      ) : (
        <ul className="pendingList">
          {[...pending, ...stuck].map((sale) => (
            <li key={sale.client_ref} className={sale.quarantined ? 'pendingItem pendingItem--stuck' : 'pendingItem'}>
              <div className="pendingItemHead">
                <span className="pendingWhen">{when(sale.sold_at)}</span>
                <span className={`pendingPay pendingPay--${sale.payment}`}>{sale.payment}</span>
                <span className="money pendingTotal">{formatMoney(sale.total_cents)}</span>
              </div>
              <div className="pendingLines">{sale.items.map((line) => `${line.quantity}× ${line.title}`).join(', ')}</div>
              {sale.quarantined && <div className="pendingStuck">Needs attention: {sale.last_error}</div>}
              {!sale.quarantined && sale.attempts > 0 && (
                <div className="pendingAttempts">
                  {sale.attempts} failed attempt{sale.attempts === 1 ? '' : 's'}
                  {sale.last_error ? ` — ${sale.last_error}` : ''}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
};

export default PendingSync;
